/**
 * Scores one rep against the drill's posture criteria.
 *
 * The drill is not only an arm swing: it is performed on the toes, with the
 * knees flexed, the core engaged, the head and neck neutral, and the chest and
 * head not ahead of the knees. Each of those is checked here over the samples of
 * a single rep. See docs/arm-swing-posture.md for where every threshold came
 * from — all of them are measured off real footage, and all are settings.
 *
 * Side view only. Every criterion is a sagittal-plane measurement; from the
 * front they are either invisible or meaningless, so `POSTURE_VIEWS` gates them
 * the way `views:` gates the per-window metrics.
 *
 * One of the five is not what it claims. Core engagement is not observable:
 * no landmark reports muscle activation, and a lumbar arch barely moves the
 * hip-to-shoulder line the trunk axis is built from. What IS observable is its
 * failure mode — the trunk rocking as the arms pass overhead — so that is what
 * is measured, and it is labelled "Trunk steady" rather than "Core engaged".
 * Claiming otherwise would be a lie the athlete cannot check.
 */
import { nearSide } from "./sample";

export const POSTURE_VIEWS = ["side"];

function median(values) {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function range(values) {
  return Math.max(...values) - Math.min(...values);
}

/**
 * Which way the athlete faces, as +1 or -1 along x, from the nose-to-ear offset
 * taken over a span rather than per frame.
 *
 * Per frame it is not reliable enough: on real footage the per-frame sign
 * disagreed with the span's own median on 57 frames of 666. Over a span the
 * median is unambiguous, and the ear is the steadiest landmark available.
 */
export function facingSign(samples, side) {
  const offset = median(samples.map((s) => s.facingOffset[side]));
  return offset != null && offset < 0 ? -1 : 1;
}

/**
 * The scale tiptoe height is measured in, as a median over the samples given.
 *
 * Deliberately not per frame: an occluded foot measures as short as 3cm against
 * a 13cm median, and a per-frame ratio would read a raised heel where there was
 * only a bad frame.
 */
export function footScale(samples, side) {
  return median(samples.map((s) => s.footLength[side]));
}

/**
 * @param samples One rep's samples, from `RepCounter`.
 * @param thresholds `STATE.posture`.
 * @param scale Optional foot length to normalise tiptoe height by. Pass the
 *   value measured over the whole session where there is one; the rep's own
 *   samples are the fallback and are noisier.
 * @return One row per criterion: `{ id, label, value, unit, precision, pass }`.
 *   `pass` is null when the criterion could not be judged.
 */
export function scoreRep(samples, thresholds, scale = null) {
  if (samples.length === 0) {
    return [];
  }
  const side = nearSide(samples);
  const facing = facingSign(samples, side);
  const foot = scale || footScale(samples, side);

  // Four of the six read the legs, and the knee is the reference the head and
  // chest are compared against. A take framed from the knees up still counts
  // reps — see RepCounter — so those four report as unjudged rather than
  // confidently wrong on landmarks the model is guessing at.
  const legsSeen =
    median(samples.map((s) => s.legVisibility[side])) >=
    thresholds.minVisibility;

  const peakLift = Math.max(...samples.map((s) => s.heelLift[side]));
  const tiptoe = legsSeen && foot > 0 ? peakLift / foot : null;

  const straightestKnee = legsSeen
    ? Math.min(...samples.map((s) => s.knee[side]))
    : null;
  const trunkRock = range(samples.map((s) => s.trunkTilt));

  // Centimetres: the athlete reads this, and metres to three decimals does not
  // communicate. Ahead is positive, so the worst frame is the largest.
  const headAhead =
    100 * Math.max(...samples.map((s) => facing * s.aheadOfKneeRaw[side].head));
  const chestAhead =
    100 *
    Math.max(...samples.map((s) => facing * s.aheadOfKneeRaw[side].chest));

  // Typical, not worst: the head moves through the swing and one frame of it
  // dipping is not a fault. Deviation from the athlete's own neutral offset,
  // which is anatomy rather than form, hence a setting.
  const neckDeviation =
    median(samples.map((s) => s.headPitch[side])) - thresholds.neckOffset;

  return [
    {
      id: "tiptoe",
      label: "On the toes",
      value: tiptoe,
      unit: "×foot",
      precision: 2,
      pass: tiptoe == null ? null : tiptoe >= thresholds.tiptoeMin,
    },
    {
      id: "knee_flex",
      label: "Knees flexed",
      value: straightestKnee,
      unit: "°",
      precision: 0,
      pass:
        straightestKnee == null
          ? null
          : straightestKnee <= thresholds.kneeFlexMax,
    },
    {
      id: "trunk_steady",
      label: "Trunk steady",
      value: trunkRock,
      unit: "°",
      precision: 1,
      pass: trunkRock <= thresholds.trunkRockMax,
    },
    {
      id: "head_back",
      label: "Head behind knee",
      value: headAhead,
      unit: "cm",
      precision: 1,
      pass: headAhead == null ? null : headAhead <= thresholds.aheadOfKneeMax,
    },
    {
      id: "chest_back",
      label: "Chest behind knee",
      value: chestAhead,
      unit: "cm",
      precision: 1,
      pass: chestAhead == null ? null : chestAhead <= thresholds.aheadOfKneeMax,
    },
    {
      id: "neck_neutral",
      label: "Neck neutral",
      value: neckDeviation,
      unit: "°",
      precision: 0,
      pass: Math.abs(neckDeviation) <= thresholds.neckTolerance,
    },
  ];
}

/**
 * Running tally across a set: how many reps passed each criterion.
 *
 * A per-rep verdict alone is not useful to read from across a gym — ten red
 * marks on a ten-rep set is noise. What a coach wants is "8 of 10 on the toes".
 */
export class PostureTally {
  constructor() {
    this.reps = 0;
    this.passes = new Map();
    this.rows = [];
  }

  add(scored) {
    this.reps += 1;
    this.rows = scored;
    for (const row of scored) {
      if (row.pass === true) {
        this.passes.set(row.id, (this.passes.get(row.id) || 0) + 1);
      }
    }
  }

  /** The latest rep's rows, each with how many reps have passed that criterion. */
  summary() {
    return this.rows.map((row) => ({
      ...row,
      passed: this.passes.get(row.id) || 0,
      total: this.reps,
    }));
  }

  reset() {
    this.reps = 0;
    this.passes.clear();
    this.rows = [];
  }
}
